"use client";

import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import {
  Activity,
  ArrowRight,
  BarChart3,
  CalendarClock,
  Gauge,
  Route,
  Search,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import HomeIndexFluctuation from "@/components/HomeIndexFluctuation";
import { computeMarketStats, type MarketStats, type ScannerMarketRow } from "@/lib/marketStats";

type MarketDriver = {
  symbol: string;
  company: string;
  return_pct: number;
  contribution_pct: number;
};

type MarketIntelligencePayload = {
  available_indices?: string[];
  source: {
    latest_session_date?: string | null;
  };
  selected_index: {
    level: number | null;
    daily_pct: number | null;
    advancers_count: number;
    decliners_count: number;
    unchanged_count: number;
    total_traded_value_usd: number | null;
    previous_total_traded_value_usd: number | null;
  };
  inputs: {
    top_weighted_contributors: MarketDriver[];
    top_weighted_detractors: MarketDriver[];
  };
  structuredMarketIntelligence: {
    marketHealth: {
      score: number;
      regimeLabel: string;
      interpretation: string;
    };
    participation: {
      participationPct: number;
      breadthDirection: "positive" | "negative" | "neutral";
    };
    liquidity: {
      vs30dAvgPct: number;
    };
    driverConcentrationImpact: {
      impactSharePct: number;
      distributionLabel: "concentrated" | "moderate" | "broad";
    };
    structuralText: string;
  };
};

const INDEX_OPTIONS = ["KSE-100", "KSE-ALL-SHARES", "KMI-30", "MII30", "PSXDIV20"];

const INDEX_LABELS: Record<string, string> = {
  "KSE-100": "KSE-100",
  "KSE-ALL-SHARES": "KSE-ALL",
  "KMI-30": "KMI-30",
  MII30: "MII30",
  PSXDIV20: "PSXDIV20",
};

const workflowLinks = [
  {
    icon: BarChart3,
    title: "Market Overview",
    label: "Full tape",
    href: "/market-overview",
  },
  {
    icon: Activity,
    title: "Index Fluctuation",
    label: "Intraday pulse",
    href: "/indices/fluctuation",
  },
  {
    icon: Activity,
    title: "Money Flow",
    label: "Who bought",
    href: "/money-flow",
  },
  {
    icon: Search,
    title: "Scanner",
    label: "Shortlist",
    href: "/scanner",
  },
  {
    icon: Route,
    title: "Strategies",
    label: "Trade plan",
    href: "/strategies",
  },
] as const;

const formatNumber = (value: number | null | undefined, digits = 2) => {
  if (value == null || Number.isNaN(value)) return "N/A";
  return value.toLocaleString(undefined, { maximumFractionDigits: digits });
};

const formatSignedPercent = (value: number | null | undefined, digits = 2) => {
  if (value == null || Number.isNaN(value)) return "N/A";
  return `${value >= 0 ? "+" : ""}${value.toFixed(digits)}%`;
};

const formatUsdCompact = (value: number | null | undefined) => {
  if (value == null || Number.isNaN(value)) return "N/A";
  const abs = Math.abs(value);
  if (abs < 500_000_000) return `$${formatNumber(value / 1_000_000, 2)}m`;
  return `$${formatNumber(value / 1_000_000_000, 2)}bn`;
};

const metricToneClass = (value: number | null | undefined) => {
  if (value == null || Number.isNaN(value) || value === 0) return "is-neutral";
  return value > 0 ? "is-positive" : "is-negative";
};

const marketHealthBand = (score: number | null | undefined) => {
  const value = Number(score ?? 0);
  if (value < 40) return "Weak";
  if (value < 60) return "Neutral";
  if (value < 80) return "Healthy";
  return "Strong";
};

const regimeShortPhrase = (regime: string | null | undefined) => {
  const value = String(regime || "").toLowerCase();
  if (value.includes("distribution")) return "Distribution risk";
  if (value.includes("weakness")) return "Soft breadth";
  if (value.includes("base")) return "Base building";
  if (value.includes("narrow")) return "Narrow leadership";
  if (value.includes("volatility")) return "High dispersion";
  if (value.includes("strength") || value.includes("broad")) return "Broad support";
  if (value.includes("participation") && value.includes("liquid")) return "Weak breadth & liquidity";
  if (value.includes("participation")) return "Weak participation";
  if (value.includes("liquid")) return "Liquidity contracted";
  return regime || "Session read";
};

const sessionView = (data: MarketIntelligencePayload | null) => {
  const structured = data?.structuredMarketIntelligence;
  if (!data || !structured) {
    return {
      tone: "neutral" as const,
      title: "Loading session read",
      line: "Pulling the latest EOD market context.",
    };
  }

  if (
    structured.marketHealth.score >= 60 &&
    structured.participation.breadthDirection === "positive" &&
    structured.liquidity.vs30dAvgPct >= -10
  ) {
    return {
      tone: "positive" as const,
      title: "Market supports risk",
      line: "Breadth and liquidity are constructive. Start with leaders, then confirm money flow.",
    };
  }

  if (
    structured.marketHealth.score < 40 ||
    structured.participation.breadthDirection === "negative" ||
    structured.liquidity.vs30dAvgPct <= -20
  ) {
    return {
      tone: "negative" as const,
      title: "Market asks for defense",
      line: "Quality is weak or liquidity is fading. Reduce chasing and require cleaner confirmation.",
    };
  }

  return {
    tone: "neutral" as const,
    title: "Selective tape",
    line: "Signals are mixed. Use breadth and flow to narrow the watchlist before scanning.",
  };
};

export default function HomeSessionBrief() {
  const [selectedIndex, setSelectedIndex] = useState("KSE-100");
  const [marketIntel, setMarketIntel] = useState<MarketIntelligencePayload | null>(null);
  const [marketStats, setMarketStats] = useState<MarketStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetch(`/api/market-intelligence?index=${encodeURIComponent(selectedIndex)}`, {
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        setMarketIntel((await response.json()) as MarketIntelligencePayload);
      } catch (err) {
        if (controller.signal.aborted) return;
        setMarketIntel(null);
        setError(err instanceof Error ? err.message : "Unable to load market brief");
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    void load();
    return () => controller.abort();
  }, [selectedIndex]);

  useEffect(() => {
    const controller = new AbortController();

    const loadStats = async () => {
      try {
        setStatsLoading(true);
        const response = await fetch("/api/scanner/market?limit=2000&use_snapshot=1", {
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const json = await response.json();
        const rows: ScannerMarketRow[] = Array.isArray(json?.rows) ? json.rows : [];
        setMarketStats(computeMarketStats(rows, json?.latest_date ?? null, json?.stats ?? null));
      } catch {
        if (!controller.signal.aborted) setMarketStats(null);
      } finally {
        if (!controller.signal.aborted) setStatsLoading(false);
      }
    };

    void loadStats();
    return () => controller.abort();
  }, []);

  const structured = marketIntel?.structuredMarketIntelligence ?? null;
  const brief = useMemo(() => sessionView(marketIntel), [marketIntel]);
  const availableIndices = marketIntel?.available_indices?.length ? marketIntel.available_indices : INDEX_OPTIONS;
  const selectedIndexLabel = INDEX_LABELS[selectedIndex] || selectedIndex;

  const contributors = marketIntel?.inputs.top_weighted_contributors.slice(0, 3) ?? [];
  const detractors = marketIntel?.inputs.top_weighted_detractors.slice(0, 3) ?? [];
  const totalStocks =
    (marketIntel?.selected_index.advancers_count ?? 0) +
    (marketIntel?.selected_index.decliners_count ?? 0) +
    (marketIntel?.selected_index.unchanged_count ?? 0);

  return (
    <section className={`homeSessionBrief homeSessionBrief--${brief.tone}`} aria-labelledby="home-session-title">
      <div className="homeSessionShell">
        <div className="homeSessionTopbar">
          <div>
            <span className="homeSessionKicker">Latest PSX Session Brief</span>
            <h1 id="home-session-title">Read the last close before tomorrow&apos;s watchlist.</h1>
          </div>
          <label className="homeSessionIndexControl">
            <span>Index</span>
            <select value={selectedIndex} onChange={(event) => setSelectedIndex(event.target.value)}>
              {availableIndices.map((index) => (
                <option key={index} value={index}>
                  {INDEX_LABELS[index] || index}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="homeSessionGrid">
          <div className="homeSessionRead">
            <div className="homeSessionReadHeader">
              <div>
                {loading ? (
                  <span className="skeleton skeleton--line-xs" style={{ display: "block", marginBottom: "0.4rem" }} />
                ) : (
                  <span>{marketIntel?.source.latest_session_date ? `Session ${marketIntel.source.latest_session_date}` : "EOD context"}</span>
                )}
                {loading ? (
                  <span className="skeleton skeleton--line" style={{ display: "block", height: "1.4em" }} />
                ) : (
                  <strong>{brief.title}</strong>
                )}
              </div>
              <CalendarClock size={22} strokeWidth={1.8} aria-hidden="true" />
            </div>
            {loading ? (
              <span className="skeleton skeleton--line" style={{ display: "block", marginBottom: "0.1rem" }} />
            ) : (
              <p>{brief.line}</p>
            )}

            <div className="homeSessionIndexLine">
              <div>
                <span>{selectedIndexLabel}</span>
                <strong className="tabular-nums">{formatNumber(marketIntel?.selected_index.level, 2)}</strong>
              </div>
              <div className={metricToneClass(marketIntel?.selected_index.daily_pct)}>
                {formatSignedPercent(marketIntel?.selected_index.daily_pct)}
              </div>
            </div>

            <div className="homeSessionActions">
              <Link href="/market-overview" className="homeSessionPrimary">
                Open Market Overview
                <ArrowRight size={17} strokeWidth={2.2} aria-hidden="true" />
              </Link>
              <Link href="/money-flow" className="homeSessionSecondary">
                Check Money Flow
              </Link>
            </div>
          </div>

          <div className="homeSessionMetrics" aria-label="Session metrics">
            <div className="homeSessionMetric homeSessionMetric--wide">
              <div className="homeSessionMetricLabel">
                <Gauge size={18} strokeWidth={1.8} aria-hidden="true" />
                <span>Market Health</span>
              </div>
              {loading ? (
                <span className="skeleton skeleton--value" style={{ display: "block" }} />
              ) : (
                <strong className="tabular-nums">
                  {structured ? `${formatNumber(structured.marketHealth.score, 1)}/100` : "N/A"}
                </strong>
              )}
              <small>{structured ? regimeShortPhrase(structured.marketHealth.regimeLabel) || marketHealthBand(structured.marketHealth.score) : "EOD signal"}</small>
            </div>

            <div className="homeSessionMetric">
              <span>Breadth</span>
              {loading ? (
                <span className="skeleton skeleton--value" style={{ display: "block" }} />
              ) : (
                <strong className="tabular-nums">
                  {structured ? `${formatNumber(structured.participation.participationPct, 1)}%` : "N/A"}
                </strong>
              )}
              <small>{totalStocks > 0 ? `${marketIntel?.selected_index.advancers_count} adv / ${marketIntel?.selected_index.decliners_count} dec` : "Advancers vs decliners"}</small>
            </div>

            <div className="homeSessionMetric">
              <span>Traded Value</span>
              {loading ? (
                <span className="skeleton skeleton--value" style={{ display: "block" }} />
              ) : (
                <strong className="tabular-nums">{formatUsdCompact(marketIntel?.selected_index.total_traded_value_usd)}</strong>
              )}
              <small className={metricToneClass(structured?.liquidity.vs30dAvgPct)}>
                {structured ? `${formatSignedPercent(structured.liquidity.vs30dAvgPct)} vs 30D` : "Liquidity read"}
              </small>
            </div>

            <div className="homeSessionMetric">
              <span>Driver Spread</span>
              {loading ? (
                <span className="skeleton skeleton--value" style={{ display: "block" }} />
              ) : (
                <strong>{structured?.driverConcentrationImpact.distributionLabel ?? "N/A"}</strong>
              )}
              <small className="tabular-nums">
                {structured ? `${formatNumber(structured.driverConcentrationImpact.impactSharePct, 1)}% top-driver share` : "Index pressure"}
              </small>
            </div>

            <div className="homeSessionMetric">
              <span>Extremes</span>
              {statsLoading ? (
                <span className="skeleton skeleton--value" style={{ display: "block" }} />
              ) : (
                <div className="homeSessionCircuitPair" aria-label="Upper and lower circuit counts">
                  <span>
                    <strong className="tabular-nums">{marketStats?.upperCircuit ?? 0}</strong>
                    UC
                  </span>
                  <span>
                    <strong className="tabular-nums">{marketStats?.lowerCircuit ?? 0}</strong>
                    LC
                  </span>
                </div>
              )}
              <small>{marketStats?.latestDate ? `Stats ${marketStats.latestDate}` : "Circuit pressure"}</small>
            </div>
          </div>
        </div>

        {error && <div className="homeSessionError">Market brief unavailable: {error}</div>}

        <HomeIndexFluctuation embedded />

        <div className="homeSessionLower">
          <div className="homeSessionDrivers">
            <div className="homeSessionSectionHead">
              <span>Index Movers</span>
              <Link href="/market-overview#drivers">All drivers</Link>
            </div>
            <div className="homeSessionDriverColumns">
              <div className="homeSessionDriverCol homeSessionDriverCol--leaders">
                <div className="homeSessionDriverTitle">
                  <TrendingUp size={17} strokeWidth={1.9} aria-hidden="true" />
                  Leaders
                </div>
                {loading ? (
                  [1, 2, 3].map((i) => (
                    <div key={i} className="homeSessionDriverRow">
                      <span className="skeleton skeleton--line-short" style={{ display: "block", height: "0.9em" }} />
                    </div>
                  ))
                ) : contributors.map((row) => (
                  <div key={`leader-${row.symbol}`} className="homeSessionDriverRow">
                    <div className="homeSessionDriverRowInfo">
                      <span>{row.symbol}</span>
                      {row.company ? <small>{row.company}</small> : null}
                    </div>
                    <div className="homeSessionDriverRowValues">
                      <strong className="is-positive tabular-nums">{formatSignedPercent(row.return_pct)}</strong>
                      {row.contribution_pct != null ? (
                        <small className="tabular-nums">{formatSignedPercent(row.contribution_pct)} idx</small>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
              <div className="homeSessionDriverCol homeSessionDriverCol--drags">
                <div className="homeSessionDriverTitle">
                  <TrendingDown size={17} strokeWidth={1.9} aria-hidden="true" />
                  Drags
                </div>
                {loading ? (
                  [1, 2, 3].map((i) => (
                    <div key={i} className="homeSessionDriverRow">
                      <span className="skeleton skeleton--line-short" style={{ display: "block", height: "0.9em" }} />
                    </div>
                  ))
                ) : detractors.map((row) => (
                  <div key={`drag-${row.symbol}`} className="homeSessionDriverRow">
                    <div className="homeSessionDriverRowInfo">
                      <span>{row.symbol}</span>
                      {row.company ? <small>{row.company}</small> : null}
                    </div>
                    <div className="homeSessionDriverRowValues">
                      <strong className="is-negative tabular-nums">{formatSignedPercent(row.return_pct)}</strong>
                      {row.contribution_pct != null ? (
                        <small className="tabular-nums">{formatSignedPercent(row.contribution_pct)} idx</small>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="homeSessionWorkflow">
            <div className="homeSessionSectionHead">
              <span>Next Checks</span>
              <small>EOD workflow</small>
            </div>
            <div className="homeSessionWorkflowGrid">
              {workflowLinks.map((item) => {
                const Icon = item.icon;
                return (
                  <Link key={item.href} href={item.href} className="homeSessionWorkflowLink">
                    <Icon size={19} strokeWidth={1.8} aria-hidden="true" />
                    <span>
                      <strong>{item.title}</strong>
                      <small>{item.label}</small>
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
